import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDatePickerContext } from "./DatePickerContext";
import { Dayjs } from "dayjs";
import dayjs from "@dayjs";
import { MAX_STAY_DURATION_DAYS } from "@/constants/stay";
import { Cross, CalendarsIcon } from "../Icons";
import { useTheme } from "next-themes";
import CalendarModal from "./CalendarModal";
import { useLocale, useTranslations } from "next-intl";
import { getWeekdays } from "@/app/utils/date/getWeekdays";

const MobileCalendars: React.FC = () => {
  const { selectedDate, handleDateChange, error } = useDatePickerContext();
  const [isOpen, setIsOpen] = useState(false);
  const [localDate, setLocalDate] =
    useState<[Dayjs | null, Dayjs | null]>(selectedDate);
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLDivElement>(null);
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const locale = useLocale();

  useEffect(() => {
    if (!localDate[0] || !isOpen) {
      setLocalDate(selectedDate);
    }
  }, [selectedDate, isOpen]);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.scrollTop = scrollPosition;
    }
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, scrollPosition]);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const firstMonth = containerRef.current.querySelector(".month-calendar");
      if (firstMonth) {
        const rect = firstMonth.getBoundingClientRect();
        console.log("First Month position:", {
          top: rect.top,
          height: rect.height,
        });
      }
      const weekdays = containerRef.current.querySelectorAll(".weekday-cell");
      const firstDay = containerRef.current.querySelector(".day-cell");
      if (weekdays.length > 0 && firstDay) {
        const weekdayRect = weekdays[0].getBoundingClientRect();
        const dayRect = firstDay.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        console.log("Alignment check:", {
          weekdayLeft: weekdayRect.left,
          weekdayWidth: weekdayRect.width,
          weekdayRight: weekdayRect.right,
          dayLeft: dayRect.left,
          dayWidth: dayRect.width,
          dayRight: dayRect.right,
          leftDiff: Math.abs(weekdayRect.left - dayRect.left),
          widthDiff: Math.abs(weekdayRect.width - dayRect.width),
          containerWidth: containerRect.width,
          gridLeftOffset: weekdayRect.left - containerRect.left,
          gridRightOffset: containerRect.right - weekdayRect.right,
        });
      }
    }
  }, [isOpen]);

  const toggleModal = () => {
    if (isOpen) {
      setScrollPosition(containerRef.current?.scrollTop ?? 0);
    } else if (containerRef.current) {
      containerRef.current.scrollTop = scrollPosition;
    }
    setIsOpen((prev) => !prev);
  };

  const handleConfirm = () => {
    if (localDate[0] && !localDate[1]) {
      const nextDay = dayjs(localDate[0]).add(1, "day");
      handleDateChange([localDate[0], nextDay]);
    } else {
      handleDateChange(localDate);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalDate(selectedDate);
    setIsOpen(false);
  };

  const formatDisplayDate = (date: Dayjs | null): string => {
    if (!date) {
      return "";
    }

    return dayjs(date)
      .locale(locale)
      .format(locale === "en" ? "ddd, MMM D" : "YYYY-MM-DD");
  };

  const getDaysInMonth = (month: Dayjs): Dayjs[] => {
    const startOfMonth = month.startOf("month");
    const endOfMonth = month.endOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfWeek = endOfMonth.endOf("week");

    const days: Dayjs[] = [];
    let day = startOfWeek;
    while (day.isBefore(endOfWeek) || day.isSame(endOfWeek, "day")) {
      days.push(day);
      day = day.add(1, "day");
    }
    return days;
  };

  const handleDayClick = (day: Dayjs) => {
    const newCheckIn = day.startOf("day");
    const currentCheckIn = localDate[0];
    const currentCheckOut = localDate[1];

    if (currentCheckIn && currentCheckOut) {
      setLocalDate([newCheckIn, null]);
    } else if (currentCheckIn && !currentCheckOut) {
      if (newCheckIn.isAfter(currentCheckIn)) {
        setLocalDate([currentCheckIn, newCheckIn]);
      } else {
        setLocalDate([newCheckIn, null]);
      }
    } else {
      setLocalDate([newCheckIn, null]);
    }
  };

  const disabledDays = [
    { before: dayjs().startOf("day").toDate() },
    { after: dayjs().add(1, "year").toDate() },
    localDate[0] && !localDate[1]
      ? {
          after: dayjs(localDate[0])
            .add(MAX_STAY_DURATION_DAYS, "day")
            .toDate(),
        }
      : null,
  ].filter(
    (
      rule
    ): rule is
      | { before: Date; after?: undefined }
      | { after: Date; before?: undefined } =>
      rule !== null && rule !== undefined
  );

  const isDayDisabled = (day: Dayjs): boolean =>
    disabledDays.some((rule) => {
      if (rule.before && day.isBefore(rule.before, "day")) return true;
      if (rule.after && day.isAfter(rule.after, "day")) return true;
      return false;
    });

  const isDaySelected = (
    day: Dayjs
  ): {
    isSelected: boolean;
    isStart: boolean;
    isEnd: boolean;
    isMiddle: boolean;
    isToday: boolean;
  } => {
    const checkIn = localDate[0];
    const checkOut = localDate[1];
    const isToday = day.isSame(dayjs().startOf("day"), "day");

    if (!checkIn) {
      return {
        isSelected: false,
        isStart: false,
        isEnd: false,
        isMiddle: false,
        isToday,
      };
    }

    if (checkIn && !checkOut) {
      return {
        isSelected: day.isSame(checkIn, "day"),
        isStart: day.isSame(checkIn, "day"),
        isEnd: false,
        isMiddle: false,
        isToday,
      };
    }

    return {
      isSelected:
        day.isSame(checkIn, "day") ||
        day.isSame(checkOut, "day") ||
        (day.isAfter(checkIn, "day") && day.isBefore(checkOut, "day")),
      isStart: day.isSame(checkIn, "day"),
      isEnd: day.isSame(checkOut, "day"),
      isMiddle: day.isAfter(checkIn, "day") && day.isBefore(checkOut, "day"),
      isToday,
    };
  };

  const t = useTranslations();

  const weekdays = getWeekdays(t);

  const ConfirmButton: React.FC = () => (
    <div
      ref={confirmButtonRef}
      className="fixed bottom-0 left-0 z-[2000] w-full border-t border-sleap-gray-200 bg-lightwhite px-4 py-3 dark:border-sleap-gray-600 dark:bg-sleap-gray-950"
      style={{
        bottom: "env(safe-area-inset-bottom, 0px)",
        boxSizing: "border-box",
        contain: "paint",
        isolation: "isolate",
      }}
    >
      <button
        type="button"
        className="flex w-full items-center justify-center rounded-xl bg-sky-blue px-3 py-2 font-semibold text-white dark:bg-primary dark:text-sleap-fern-100"
        onClick={handleConfirm}
      >
        <span className="text-base font-semibold"> {t("Other.confirm")}</span>
      </button>
    </div>
  );

  const MonthCalendar: React.FC<{ monthOffset: number; isLast?: boolean }> = ({
    monthOffset,
    isLast = false,
  }) => {
    const month = dayjs().add(monthOffset, "month");

    return (
      <div
        className={`month-calendar px-4 py-4 ${isLast ? "mb-8" : "mb-0"} bg-lightwhite dark:bg-sleap-gray-950 ${
          !isLast
            ? "border-b border-sleap-gray-200 dark:border-sleap-gray-600"
            : ""
        }`}
      >
        <div
          className={`mb-4 flex items-center justify-center ${
            monthOffset === 0 ? "pt-2" : "pt-4"
          }`}
        >
          <span className="text-[16px] font-bold leading-[24px] text-sleap-gray-950 dark:text-lightwhite">
            {month.format("MMMM YYYY")}
          </span>
        </div>
        <div className="grid grid-cols-7 gap-0 text-center">
          {getDaysInMonth(month).map((day, index) => {
            const isCurrentMonth = day.month() === month.month();
            const isDisabled = isDayDisabled(day);
            const { isSelected, isStart, isEnd, isMiddle, isToday } =
              isDaySelected(day);

            if (!isCurrentMonth) {
              return <div key={index} className="min-h-[40px]" />;
            }

            return (
              <button
                type="button"
                key={index}
                onClick={() => !isDisabled && handleDayClick(day)}
                disabled={isDisabled}
                className={`day-cell relative flex min-h-[40px] cursor-pointer items-center justify-center text-[14px] font-normal leading-[20px] ${
                  isCurrentMonth
                    ? "text-sleap-gray-950 dark:text-lightwhite"
                    : "text-sleap-gray-500 dark:text-sleap-gray-500"
                } ${
                  isDisabled
                    ? "cursor-not-allowed text-sleap-gray-500 dark:text-sleap-gray-500"
                    : ""
                } ${
                  !isSelected && !isDisabled
                    ? "hover:rounded-full hover:bg-sleap-gray-100 dark:hover:bg-sleap-gray-700"
                    : ""
                }`}
                aria-label={`Select ${day.format("MMMM D, YYYY")}`}
              >
                {isSelected && isStart && !isEnd && (
                  <div className="absolute top-0 z-10 h-10 w-10 rounded-full bg-primary dark:bg-sleap-fern-100" />
                )}
                {isSelected &&
                  (isStart || isMiddle || isEnd) &&
                  localDate[1] && (
                    <div
                      className={`absolute top-0 z-10 h-10 w-full bg-sleap-sky-blue-200 dark:bg-sleap-fern-100 ${
                        isStart ? "rounded-l-full left-[3px]" : ""
                      } ${isEnd ? "rounded-r-full right-[3px]" : ""}`}
                    />
                  )}
                {isSelected &&
                  isStart &&
                  isEnd &&
                  day.isSame(localDate[0], "day") &&
                  day.isSame(localDate[1], "day") && (
                    <div className="absolute top-0 z-10 h-10 w-10 rounded-full bg-primary dark:bg-sleap-fern-100" />
                  )}
                <div className="relative flex h-10 w-10 items-center justify-center">
                  {(isStart || isEnd) && (
                    <div className="absolute z-20 h-10 w-10 rounded-full bg-sky-blue dark:bg-primary" />
                  )}
                  <span
                    className={`relative z-30 w-full text-center ${
                      isStart || isEnd
                        ? "font-semibold text-white dark:text-sleap-fern-100"
                        : isToday && !isDisabled && !isSelected
                          ? "font-normal text-sky-blue dark:text-primary"
                          : isMiddle
                            ? "font-normal text-sleap-gray-950 dark:text-lightwhite"
                            : isDisabled
                              ? "font-normal text-sleap-gray-500 dark:text-sleap-gray-500"
                              : "font-normal text-sleap-gray-950 dark:text-lightwhite"
                    }`}
                  >
                    {day.date()}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={toggleModal}
        className={`mobile-date-picker flex h-14 w-full flex-row items-center gap-4 rounded-lg border-[1px] bg-lightwhite px-4 text-sleap-gray-950 md:w-full 2xl:border-2 ${
          error
            ? "border-sleap-red-200"
            : "border-sleap-gray-400 hover:border-sleap-gray-600 dark:border-input dark:hover:border-white"
        } dark:bg-sleap-gray-950 dark:text-lightwhite`}
        aria-label={t("Other.openDatePicker")}
        aria-invalid={error ? "true" : "false"}
      >
        {localDate[0] ? (
          <div className="flex w-full items-center">
            <span className="-ms-0.5">
              <CalendarsIcon color={isDarkMode ? "lightWhite" : "black"} />
            </span>
            <div className="mx-auto flex items-center justify-center mobile-date-input-container">
              <p className="mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium">
                {formatDisplayDate(localDate[0])}
              </p>
              <span className="px-2">-</span>
              <p className="mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium">
                {formatDisplayDate(localDate[1])}
              </p>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex items-center gap-3">
            <CalendarsIcon color={isDarkMode ? "lightWhite" : "black"} />
            <div className="flex items-center">
              <p className="mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium">
                {t("Other.startDate")}{" "}
              </p>
              <span className="px-2">-</span>
              <p className="mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium">
                {t("Other.endDate")}{" "}
              </p>
            </div>
          </div>
        )}
      </button>

      <CalendarModal
        isOpen={isOpen}
        width="w-full h-full"
        bgColor="bg-lightwhite dark:bg-sleap-gray-950"
      >
        {isOpen && createPortal(<ConfirmButton />, document.body)}
        <div
          ref={containerRef}
          className="mobile-date-picker-container h-full overflow-y-auto bg-lightwhite dark:bg-sleap-gray-950"
          style={{ contain: "layout", isolation: "isolate" }}
        >
          <div className="sticky top-0 z-50 bg-lightwhite dark:bg-sleap-gray-950">
            <div className="flex items-center justify-between px-4 pb-4 pt-4">
              <h4 className="mb-0 text-lg font-bold text-sleap-gray-950 dark:text-lightwhite">
                {t("Other.calendar")}
              </h4>
              <button
                type="button"
                className="rounded-full bg-sleap-gray-200 p-2 dark:bg-sleap-gray-700"
                onClick={handleCancel}
                aria-label={t("Other.closeCalendar")}
              >
                <Cross color={isDarkMode ? "lightWhite" : "black"} />
              </button>
            </div>
            <div className="w-full left-0 right-0 bg-sleap-off-white-100 dark:bg-sleap-gray-850">
              <div className="grid grid-cols-7 gap-0 px-4 text-sleap-gray-950 dark:text-lightwhite">
                {weekdays.map((day, index) => (
                  <div
                    key={index}
                    className="weekday-cell flex min-h-[40px] items-center justify-center text-center"
                  >
                    <div className="relative flex h-10 w-10 items-center justify-center">
                      <span className="text-[14px] font-semibold leading-[20px]">
                        {day}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-lightwhite pb-16 pt-4 dark:bg-sleap-gray-950">
            {Array.from({ length: 13 }).map((_, index) => (
              <MonthCalendar
                key={index}
                monthOffset={index}
                isLast={index === 12}
              />
            ))}
          </div>
        </div>
      </CalendarModal>
    </>
  );
};

export default MobileCalendars;
